import { Component, Inject, Input, OnDestroy } from '@angular/core';

import { StageFeedback, StageService
       } from 'app/collage-designer/stage/stage.service';
import { ProgressType
       } from 'app/controls/progress-overlay/progress-overlay.component';
import { ProductDesignerService } from 'app/product-designer.service';
import { ProgressConstants } from 'core/constants';
import { PriceCalculationService } from 'core/price/price-calculation.service';

import { ProductService } from 'core/product/product.service';
import { PostedProducts } from 'core/productdesignerd/productdesignerd.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Subscription } from 'rxjs';
import { Progress, ProgressService } from '../progress.service';
import { ValidationFailureReason } from '../validate-design-step.directive';

@Component({
    selector: 'progress-buttons',
    templateUrl: './progress-buttons.component.html',
    styleUrls: ['./progress-buttons.component.scss'],
    standalone: false
})
export class ProgressButtonsComponent implements OnDestroy
{
  public previousProgress: Progress;
  public nextProgress: Progress;
  @Input() public progressChangeable: boolean = true;

  private subscriptions: Subscription = new Subscription();

  constructor(
      @Inject(ProgressService) private progressService,
      @Inject(StageService) private stageService,
      @Inject(ProductService) private productService,
      @Inject(EventTrackingService) private eventTrackingService,
      @Inject(PriceCalculationService) public priceCalculationService,
      @Inject(ProductDesignerService) private productDesignerService:
          ProductDesignerService
      )
  {
    this.updateProgressState();

    this.subscriptions.add(
        this.progressService.progressChanged.subscribe((progress) => {
          this.updateProgressState();
        }));
  }

  private updateProgressState(): void
  {
    this.previousProgress = this.progressService.getPreviousProgress();
    this.nextProgress = this.progressService.getNextProgress();
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  public onValidationFailed(reason: ValidationFailureReason): void
  {
    switch (reason) {
      case ValidationFailureReason.PLACEHOLDERS_UNCHANGED:
        this.stageService.requestFeedback(
            StageFeedback.HIGHLIGHT_UNCHANGED_PLACEHOLDERS);
        break;

      default:
        break;
    }
  }

  public async setCurrentProgress(progressId: string): Promise<void>
  {
    this.productDesignerService.suspendDesignerTrigger.emit(
        {suspend: true, progressType: ProgressType.Spinner});
    try {
      const currentProgress = this.progressService.getCurrentProgress();
      if (currentProgress.group === ProgressConstants.GROUP_FEATURES) {
        this.trackSelectedFeature(String(currentProgress.groupMember));
      }

      await this.progressService.setCurrentProgress(progressId);
      this.trackProgressChangeEnd(currentProgress.id, progressId);
    }
    finally {
      this.productDesignerService.suspendDesignerTrigger.emit(
          {suspend: false, progressType: ProgressType.Spinner});
    }
  }

  public finalizationDone(result: PostedProducts|undefined): void
  {
    if (result) {
      this.trackProgressChangeEnd(
          this.progressService.getCurrentProgress().id, 'finalization');
      window.parent.postMessage({
          'type': 'FINALIZED',
          'data': {
            'products': result.products,
          }
      }, '*');
    }

    this.progressService.isFinalized = true;

    if (result && result.redirect_location) {
      window.top.location.href = result.redirect_location;
    }

    this.productDesignerService.suspendDesignerTrigger.emit({
      suspend: false,
      progressType: ProgressType.FinalProgress,
      finished: true,
    });
  }

  private trackSelectedFeature(featureGroup: string): void
  {
    const currentProduct = this.productService.getCurrentProduct();
    const selectedFeature = currentProduct
        .featureGroups[featureGroup]
        .selectedFeature;

    this.eventTrackingService.track({
      category: currentProduct.id,
      action: 'feature_group_' + featureGroup,
      label: selectedFeature,
    });
  }

  public trackProgressChangeEnd(oldProgressId: string, newProgressId: string):
      void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'progress_change_finished',
      label: `${oldProgressId} -> ${newProgressId}`,
    });
  }

  public trackProgressChangeStart(progressId: string):
      void
  {
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'progress_change_start_' + progressId,
    });
  }
}
